import React from 'react';
import { Autocomplete, AutocompleteProps, TextField, TextFieldProps } from '@mui/material';

import { useDebouncedState } from '@shared/hooks/useDebouncedState';

import { useAddressSearch } from '../hooks/useAddressSearch';


export type SearchAddressAutocompleteProps = Omit<AutocompleteProps<any, false, false, true>, 'isOptionEqualToValue' | 'getOptionLabel' | 'renderInput' | 'options' | 'onInputChange' | 'onChange' | 'loading'> & {
  label?: TextFieldProps['label'];
  TextFieldProps?: TextFieldProps;
  onChange: (option: CVT.MaybeNull<any>) => void;
  hideAdornment?: boolean;
};

export const SearchAddressAutocomplete: React.FC<SearchAddressAutocompleteProps> = ({ value, defaultValue, label, onChange, hideAdornment, TextFieldProps, ...props }) => {
  const [search, setSearch, debouncedSearch, isDebouncing] = useDebouncedState('');
  const { isLoading, results } = useAddressSearch(debouncedSearch, { enabled: debouncedSearch.length > 0, suspense: false });

  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <Autocomplete
      {...props}
      value={value}
      defaultValue={defaultValue}
      getOptionLabel={option => option?.full_name || option.name}      
      filterOptions={options => options}
      popupIcon={null}
      renderInput={(props) =>
        <TextField
          {...props}
          {...TextFieldProps}
          label={label}
          slotProps={{
            input: {
              inputRef,
              ...props.InputProps,
              ...TextFieldProps?.slotProps?.input,
            },
            inputLabel: {
              ...props.InputLabelProps,
              ...TextFieldProps?.slotProps?.inputLabel,
            },
          }}
        />
      }
      inputValue={search || ''}
      onInputChange={(_, value) => setSearch(value)}
      options={(isDebouncing || isLoading) ? [] : results}
      onChange={(e, value) => {
        if (typeof value !== 'string') {
          onChange(value);
          value && inputRef.current?.blur();
        }
      }}
      onBlur={e => {
        if (!value) {
          const option = results[0];
          onChange(option);
        }
        props?.onBlur && props.onBlur(e);
      }}
      loading={isDebouncing || (!!debouncedSearch && isLoading)}
      noOptionsText="No locations"
      slotProps={{
        paper: {
          elevation: 1,
        },
      }}
    />
  );
};
